import { Controller, FormProvider, UseFormReturn } from 'react-hook-form'

import { useInvitesPermissions } from '@/api/models/users/hooks/useInvitesPermissions'
import { PermissionsExpiresAtPicker } from '@/components/shared/datepicker'
import { AppInput, AppInputPhoneMasked } from '@/components/shared/input'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'

import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import {
  CONTACT_DESCRIPTION_MAX_LENGTH,
  CONTACT_DESCRIPTION_MAX_LENGTH_MESSAGE_FROM
} from '../model/constants'
import { type InviteCreationFormData } from '../model/schemas'
import { InviteCreationSkeleton } from './InviteCreationSkeleton'
import { PermissionsSelector } from './PermissionsSelector'

type Props = {
  form: UseFormReturn<InviteCreationFormData>
  personalAccountsError?: string
  onSubmit: (formData: InviteCreationFormData) => void
}

export const InviteCreationForm = ({
  form,
  personalAccountsError,
  onSubmit
}: Props) => {
  const { data: permissions, isLoading } = useInvitesPermissions()

  const { control, handleSubmit, clearErrors } = form

  if (isLoading) {
    return <InviteCreationSkeleton />
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mb-[120px] flex h-full flex-col gap-20 overflow-y-auto px-40 pb-40 lg:px-24 lg:pb-24 sm:px-16 sm:pb-20"
    >
      <Controller
        control={control}
        name="phone"
        render={({ field, fieldState }) => (
          <AppInputPhoneMasked
            dataTestId="invite-creation-phone-input-id"
            value={field.value}
            placeholder="Телефон"
            className="!h-56 w-full border border-grey-20 bg-white"
            invalid={Boolean(fieldState.error?.message)}
            invalidMessage={fieldState.error?.message}
            onAccept={value => {
              field.onChange(value)
              clearErrors('phone')
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="customName"
        render={({ field }) => (
          <AppInput
            dataTestId="invite-creation-description-input-id"
            value={field.value}
            onChange={field.onChange}
            placeholder="Описание контакта"
            className="!h-56 w-full border border-grey-20 bg-white"
            maxLength={CONTACT_DESCRIPTION_MAX_LENGTH}
            maxLengthMessageFrom={CONTACT_DESCRIPTION_MAX_LENGTH_MESSAGE_FROM}
          />
        )}
      />
      <Controller
        control={control}
        name="permissionsExpiresAt"
        render={({ field, fieldState }) => (
          <PermissionsExpiresAtPicker
            dataTestId="invite-creation-expires-at-input-id"
            value={field.value}
            onChange={value => {
              field.onChange(value)
              clearErrors('permissionsExpiresAt')
            }}
            invalid={Boolean(fieldState.error?.message)}
            invalidMessage={fieldState.error?.message}
          />
        )}
      />
      <FormProvider {...form}>
        <PermissionsSelector permissions={permissions || []} />
      </FormProvider>
      {personalAccountsError && (
        <AppInputMessage
          message={personalAccountsError}
          iconClassName="text-red-70"
          messageClassName="text-red-70"
        />
      )}

      <FixedBottomDrawerButton type="submit" text="Далее" />
    </form>
  )
}
